import { useState, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import { Download, FileDown } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { formatBytes, readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';
import { pdfjs } from '@/lib/pdfjs';

export function PdfCompress() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [quality, setQuality] = useState(60);
  const [scale, setScale] = useState(1.5);
  const [outputBlob, setOutputBlob] = useState<Blob | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      setFile(pendingFile);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function compress() {
    if (!file) return;
    setWorking(true);
    setOutputBlob(null);
    try {
      const buf = await readFileAsArrayBuffer(file);
      const doc = await pdfjs.getDocument({ data: buf }).promise;
      const out = await PDFDocument.create();

      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const viewport = page.getViewport({ scale });
        const c = document.createElement('canvas');
        c.width = viewport.width;
        c.height = viewport.height;
        const ctx = c.getContext('2d')!;
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, c.width, c.height);
        await page.render({ canvasContext: ctx, viewport, canvas: c }).promise;
        const dataUrl = c.toDataURL('image/jpeg', quality / 100);
        const jpgBytes = await fetch(dataUrl).then((r) => r.arrayBuffer());
        const embedded = await out.embedJpg(jpgBytes);
        const newPage = out.addPage([viewport.width, viewport.height]);
        newPage.drawImage(embedded, { x: 0, y: 0, width: viewport.width, height: viewport.height });
      }

      const bytes = await out.save({ useObjectStreams: true });
      const blob = bytesToBlob(bytes);
      setOutputBlob(blob);
      toast.success('Compressed');
    } catch (e) {
      toast.error('Compression failed');
    } finally {
      setWorking(false);
    }
  }

  const ratio = file && outputBlob ? outputBlob.size / file.size : 0;

  return (
    <div>
      <ToolHeader
        toolId="pdf-compress"
        title="Compress PDF"
        description="Re-encode pages as JPEG at a chosen quality. Best for scan-heavy or image-heavy PDFs."
        badges={
          file && outputBlob ? (
            <Badge variant={ratio < 1 ? 'success' : 'warning'}>
              {ratio < 1 ? `−${Math.round((1 - ratio) * 100)}%` : `+${Math.round((ratio - 1) * 100)}%`}
            </Badge>
          ) : null
        }
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={(f) => setFile(f[0])} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium truncate">{file.name}</div>
              <Button size="sm" variant="ghost" onClick={() => { setFile(null); setOutputBlob(null); }}>
                Change
              </Button>
            </div>
            <Field label="JPEG quality" hint={`${quality}%`}>
              <Slider value={quality} onChange={setQuality} min={20} max={95} showValue={false} />
            </Field>
            <Field label="Render scale" hint={`${scale}× · DPI ~${Math.round(72 * scale)}`}>
              <Slider value={scale} onChange={setScale} min={1} max={3} step={0.25} showValue={false} />
            </Field>
            <div className="text-2xs text-fg-subtle leading-relaxed">
              Note: this rasterizes every page. Text becomes non-searchable. Use only when size matters more than searchability.
            </div>
          </Card>
          <Card className="space-y-3">
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-fg-muted">Original</span>
                <span className="font-mono">{formatBytes(file.size)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-fg-muted">Compressed</span>
                <span className="font-mono">{outputBlob ? formatBytes(outputBlob.size) : '—'}</span>
              </div>
              {outputBlob && (
                <div className="flex items-center justify-between border-t border-border pt-1.5 mt-1.5">
                  <span className="font-medium">Saved</span>
                  <span className={`font-mono ${ratio < 1 ? 'text-success' : 'text-warning'}`}>
                    {ratio < 1
                      ? `−${formatBytes(file.size - outputBlob.size)}`
                      : `+${formatBytes(outputBlob.size - file.size)}`}
                  </span>
                </div>
              )}
            </div>
            <Button
              variant="primary"
              className="w-full"
              loading={working}
              disabled={working}
              onClick={compress}
            >
              <FileDown size={11} />
              Compress
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              disabled={!outputBlob}
              onClick={() => {
                if (!outputBlob || !file) return;
                saveAs(outputBlob, file.name.replace(/\.pdf$/i, '') + '-min.pdf');
              }}
            >
              <Download size={11} />
              Save
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PdfCompress;
